import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { chain, map } from 'lodash'
import { useMe } from '@/queries/me'
import NAV_SETTINGS from '@/constants/nav/settings'
import type { NavSettingsItem, NavSettingsSection } from '@/types/nav/settings'
import { filterNavPermissions } from '@/functions/nav'

const useNavSettings = () => {
    const me = useMe()
    const { hasBreezeBundle, hasGiving, hasFeature } = me
    const { path } = useRoute()

    const filteredNav = (section: NavSettingsSection): NavSettingsItem[] => {
        if (path === `/r/suspended`) {
            return []
        }

        return filterNavPermissions(NAV_SETTINGS[section], me)
    }

    // Show prompt to upgrade if Song Library not enabled
    const orgNav = computed(() =>
        map(filteredNav(`organization`), (item: NavSettingsItem) => {
            switch (item.id) {
                case `bulk`:
                    if (hasBreezeBundle.value) {
                        item.description = `Manage Tithe.ly data by bulk importing and deleting.`
                    }
                    if (hasGiving.value) {
                        item.route = '/bulk/update_people'
                    }
                    break
                case `users`:
                    if (hasBreezeBundle.value) {
                        item.description = `Manage what users can see when they log into Tithe.ly.`
                    }
                    break
                case `song-library`:
                    if (!hasFeature(`service_plan_integration`)) {
                        delete item.action
                        item.route = '/extensions?name=service_plan_integration'
                        item.badge = 'Upgrade'
                        item.tag = 'Click here to upgrade'
                    }
                    break
            }

            return item
        })
    )

    const userNav = computed(() => {
        const nav = filteredNav(`user`)

        if (!hasBreezeBundle.value) {
            return nav
        }

        return chain(nav)
            .reject(item => item.id === `change-password`)
            .map(item => {
                switch (item.id) {
                    case `notifications`:
                        item.action = `launchTithelyNotifications`
                        break
                    case `manage-locations`:
                        item.action = `launchAdminLocations`
                        break
                }

                return item
            })
            .value()
    })

    return { orgNav, userNav }
}

export default useNavSettings
